import React from 'react';
import { useTranslation } from 'react-i18next';

const ClusterOnboardingDiagnostics = ({ summary, loading = false, onRefresh }) => {
  const { t } = useTranslation();

  const clusterId = summary?.cluster_id || '';
  const checks = summary?.diagnostic_checks || {
    api_key_valid: false,
    helm_release_deployed: false,
    outbound_network_allowed: false
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.65)',
    border: '1px solid rgba(99, 102, 241, 0.28)',
    borderRadius: '16px',
    padding: '24px 30px',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
    color: '#FFF',
    marginBottom: '24px'
  };

  const checkItemStyle = (status) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    borderRadius: '10px',
    background: status === 'success' 
      ? 'rgba(16, 185, 129, 0.06)' 
      : status === 'fail' 
        ? 'rgba(239, 68, 68, 0.06)' 
        : 'rgba(245, 158, 11, 0.06)',
    border: `1px solid ${
      status === 'success' 
        ? 'rgba(16, 185, 129, 0.2)' 
        : status === 'fail' 
          ? 'rgba(239, 68, 68, 0.2)' 
          : 'rgba(245, 158, 11, 0.2)'
    }`,
    transition: 'all 0.25s ease'
  });

  const getStatusIcon = (status) => {
    if (status === 'success') {
      return (
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: '#10B981',
          color: '#FFF',
          fontWeight: 'bold',
          fontSize: '0.85rem'
        }}>✓</span>
      );
    }
    if (status === 'fail') {
      return (
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: '#EF4444',
          color: '#FFF',
          fontWeight: 'bold',
          fontSize: '0.85rem'
        }}>✗</span>
      );
    }
    return (
      <span className="spinner-mini" style={{
        display: 'inline-block',
        width: '20px',
        height: '20px',
        border: '2px solid rgba(245, 158, 11, 0.2)',
        borderTopColor: '#F59E0B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></span>
    );
  };

  const curlCommand = `curl -I -H "X-API-Key: <your-key-prefix>..." https://backend-core-644060845776.us-central1.run.app/api/v1/telemetry`;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1.4rem', color: '#FFF', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold' }}>
            <span>☸️</span> {t('finops.telemetry.diagnostics.title')} <span style={{ color: '#818CF8', fontSize: '0.85em', fontFamily: 'monospace', marginLeft: '6px' }}>({clusterId})</span>
          </h3>
          <p style={{ margin: 0, color: '#94A3B8', fontSize: '0.9rem', lineHeight: '1.5', maxWidth: '750px' }}>
            {t('finops.telemetry.diagnostics.subtitle')}
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#E2E8F0',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <span style={{ display: 'inline-block', transform: loading ? 'rotate(360deg)' : 'none', transition: 'transform 0.6s ease' }}>🔄</span>
          {t('finops.refresh', 'Refresh')}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        {/* Check 1: API Key Valid */}
        <div style={checkItemStyle(checks.api_key_valid ? 'success' : 'fail')}>
          {getStatusIcon(checks.api_key_valid ? 'success' : 'fail')}
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: 'bold', color: '#FFF' }}>
              {t('finops.telemetry.diagnostics.api_key_check')}
            </h4>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#94A3B8', lineHeight: '1.4' }}>
              {checks.api_key_valid 
                ? t('finops.telemetry.diagnostics.api_key_success') 
                : t('finops.telemetry.diagnostics.api_key_fail')}
            </p>
          </div>
        </div>

        {/* Check 2: Helm Release Deployed */}
        <div style={checkItemStyle(checks.helm_release_deployed ? 'success' : 'pending')}>
          {getStatusIcon(checks.helm_release_deployed ? 'success' : 'pending')}
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: 'bold', color: '#FFF' }}>
              {t('finops.telemetry.diagnostics.helm_check')}
            </h4>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#94A3B8', lineHeight: '1.4' }}>
              {checks.helm_release_deployed 
                ? t('finops.telemetry.diagnostics.helm_success') 
                : t('finops.telemetry.diagnostics.helm_pending')}
            </p>
          </div>
        </div>

        {/* Check 3: Outbound Network Allowed */}
        <div style={checkItemStyle(checks.outbound_network_allowed ? 'success' : 'pending')}>
          {getStatusIcon(checks.outbound_network_allowed ? 'success' : 'pending')}
          <div style={{ flex: 1 }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '0.95rem', fontWeight: 'bold', color: '#FFF' }}>
              {t('finops.telemetry.diagnostics.network_check')}
            </h4>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#94A3B8', lineHeight: '1.4' }}>
              {checks.outbound_network_allowed 
                ? t('finops.telemetry.diagnostics.network_success') 
                : t('finops.telemetry.diagnostics.network_pending')}
            </p>
            {!checks.outbound_network_allowed && (
              <div style={{ marginTop: '10px' }}>
                <span style={{ fontSize: '0.78rem', color: '#E2E8F0', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
                  {t('finops.telemetry.diagnostics.network_command')}
                </span>
                <div style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: '#60A5FA',
                  wordBreak: 'break-all'
                }}>
                  {curlCommand}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Polling Status Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '12px',
        background: 'rgba(99, 102, 241, 0.08)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: '10px'
      }}>
        <span className="pulse-dot" style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#6366F1',
          display: 'inline-block',
          animation: 'pulse 1.5s infinite'
        }}></span>
        <span style={{ fontSize: '0.82rem', color: '#C7D2FE', fontWeight: '600' }}>
          {t('finops.telemetry.status.live_polling')}
        </span>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { opacity: 0.4; }
          50% { opacity: 1; }
          100% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default ClusterOnboardingDiagnostics;
